import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import i18next from 'i18next';
import { enTranslations } from '../i18n/translations/en';
import { esTranslations } from '../i18n/translations/es';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

const LANG_STORAGE_KEY = 'selected_lang';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private readonly savedLang = localStorage.getItem(LANG_STORAGE_KEY) ?? 'en';
  currentLang$ = new BehaviorSubject<string>(this.savedLang);

  constructor(private ngZone: NgZone) {
    i18next.init({
      lng: this.savedLang,
      fallbackLng: 'en',
      resources: {
        en: { translation: enTranslations },
        es: { translation: esTranslations },
      },
      interpolation: {
        escapeValue: false,
      },
    });
  }

  translate(key: string, params?: object): string {
    const result = i18next.t(key, params as any);
    return typeof result === 'string' ? result : key;
  }

  /* IES-5491: TS equivalent of the translateApiValue pipe. */
  translateApiValue(value: string): string {
    if (!value?.trim()) return value;
    const key = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return this.translate(`api_values.${key}`, { defaultValue: value });
  }

  // i18next.changeLanguage() is always async (Promise-based) even with inline
  // resources. Its callback runs outside Angular's zone, so without ngZone.run()
  // no change-detection cycle fires and already-visible translated text (labels,
  // validation messages, placeholders) stays stale until the next user interaction.
  setLanguage(lang: string): void {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
    i18next.changeLanguage(lang, () => {
      this.ngZone.run(() => {
        this.currentLang$.next(lang);
      });
    });
  }

  get currentLang(): string {
    return localStorage.getItem(LANG_STORAGE_KEY) ?? this.currentLang$.value;
  }

  /**
   * Reverse-maps a (potentially translated) api_values string back to its English equivalent.
   * Safe to call with English values — they pass through unchanged.
   * Used to normalise form field values before API submission.
   */
  toEnglish(value: string): string {
    if (!value?.trim()) return value;
    const en = (enTranslations as any).api_values as Record<string, string>;
    const es = (esTranslations as any).api_values as Record<string, string>;
    if (Object.values(en).includes(value)) return value;
    const key = Object.entries(es).find(([, v]) => v === value)?.[0];
    return key ? (en[key] ?? value) : value;
  }
}
